import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, MapPin, CalendarClock, Footprints, Ticket, Plus, Trash2, Save,
  AlertTriangle, Info, CheckCircle2, XCircle, Clock, Layers, Link2, X, FlaskConical,
} from "lucide-react";
import { sr } from "../../lib/sr";

const usd = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;
const TONE = { gray: "sr-badge-gray", blue: "sr-badge-blue", green: "sr-badge-green", amber: "sr-badge-amber", red: "sr-badge-red" };
const TZS = ["America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "America/Phoenix", "America/Anchorage", "Pacific/Honolulu"];
const APPROACHES = [
  { key: "during", label: "Advertise during the event" },
  { key: "after", label: "Reach the event audience afterward" },
  { key: "both", label: "Both" },
];
const SOURCES = [
  { key: "event_location", label: "Event-location audience", desc: "Eligible devices associated with the venue during the specified dates/hours." },
  { key: "event_ad_impression", label: "Event-ad impression audience", desc: "Eligible devices served ads by the event campaign during its flight." },
];

const blankVenue = () => ({
  name: "", address: "", lat: "", lng: "", radius_m: 500,
  start_date: "", end_date: "", daily_start: "10:00", daily_end: "22:00", tz: "America/New_York",
});

function SupportPill({ v }) {
  const map = {
    true: { cls: "sr-badge-green", icon: <CheckCircle2 size={12} />, label: "Supported" },
    false: { cls: "sr-badge-red", icon: <XCircle size={12} />, label: "Unavailable" },
    limited: { cls: "sr-badge-amber", icon: <AlertTriangle size={12} />, label: "Limited" },
    operator: { cls: "sr-badge-blue", icon: <Info size={12} />, label: "Operator setup" },
  };
  const m = map[String(v)] || map.operator;
  return <span className={`sr-badge ${m.cls}`} style={{ fontSize: 10 }}>{m.icon} {m.label}</span>;
}

export default function EventTargeting({ planId, plan, editable = true, onSaved }) {
  const existing = plan?.premiums || {};
  const et = plan?.event_targeting || {};
  const [caps, setCaps] = useState(null);
  const [eventOn, setEventOn] = useState(!!(existing.event_venue || {}).enabled);
  const [ftOn, setFtOn] = useState(!!(existing.foot_traffic || {}).enabled);
  const [approach, setApproach] = useState(et.approach || "during");
  const [source, setSource] = useState(et.audience_source || "event_location");
  const [venues, setVenues] = useState(et.venues?.length ? et.venues : [blankVenue()]);
  const [retentionDays, setRetentionDays] = useState(et.retention_days ?? 7);
  const [qual, setQual] = useState(et.qualification_window || { start: "", end: "" });
  const [flight, setFlight] = useState(et.advertising_flight || { start: "", end: "" });
  const [inclusive, setInclusive] = useState(false);
  const [eventShare, setEventShare] = useState(et.delivery?.event_share ?? 60);
  const [pricing, setPricing] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [meas, setMeas] = useState(null);
  const [studyModal, setStudyModal] = useState(null);
  const [linking, setLinking] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadMeas = () => sr.planMeasurement(planId).then(setMeas).catch(() => {});
  useEffect(() => { sr.providerCapabilities().then(setCaps).catch(() => {}); }, []);
  useEffect(() => { loadMeas(); /* eslint-disable-next-line */ }, [planId]);
  useEffect(() => { sr.premiumPricing(planId, inclusive).then(setPricing).catch(() => {}); }, [planId, inclusive, eventOn, ftOn]);

  const rates = caps?.premium_rates || { event_venue_pct: 10, foot_traffic_pct: 10 };
  const capBy = useMemo(() => Object.fromEntries((caps?.capabilities || []).map((c) => [c.key, c])), [caps]);
  const historicalSupported = capBy.historical_event_audience?.supported === true;

  const updVenue = (i, k, v) => setVenues((s) => s.map((vv, idx) => (idx === i ? { ...vv, [k]: v } : vv)));
  const addVenue = () => setVenues((s) => [...s, blankVenue()]);
  const removeVenue = (i) => setVenues((s) => (s.length > 1 ? s.filter((_, idx) => idx !== i) : s));

  const eventMedia = Number(plan?.monthly_budget || 0);
  const delivery = useMemo(() => {
    const hasDay = approach === "during" || approach === "both";
    const hasFu = approach === "after" || approach === "both";
    const evS = hasDay && hasFu ? eventShare : hasDay ? 100 : 0;
    const fuS = hasDay && hasFu ? 100 - eventShare : hasFu ? 100 : 0;
    const starts = venues.map((v) => v.start_date).filter(Boolean).sort();
    const ends = venues.map((v) => v.end_date).filter(Boolean).sort();
    const evWin = { start: starts[0] || null, end: ends[ends.length - 1] || null };
    const comps = [];
    if (hasDay) comps.push({ key: "event_day", name: "Event-day delivery", share_pct: Math.round(evS), budget: (eventMedia * evS) / 100, window: evWin, description: "Runs during the event window to reach people at or around the venue in real time." });
    if (hasFu) comps.push({ key: "follow_up", name: "Follow-up delivery", share_pct: Math.round(fuS), budget: (eventMedia * fuS) / 100, window: flight, description: "Retargets the qualified event audience after the event, across the advertising flight." });
    return { components: comps, allocated: comps.reduce((a, c) => a + c.budget, 0) };
  }, [approach, eventShare, venues, flight, eventMedia]);

  const save = async () => {
    setBusy(true);
    try {
      const r = await sr.saveEventTargeting(planId, {
        event_venue_enabled: eventOn, foot_traffic_enabled: ftOn, approach, audience_source: source,
        venues: eventOn ? venues : [],
        qualification_window: qual, retention_days: Number(retentionDays) || null, advertising_flight: flight,
        delivery_event_share: Math.round(eventShare), delivery_followup_share: Math.round(100 - eventShare),
      });
      setWarnings(r.warnings || []);
      const pr = await sr.premiumPricing(planId, inclusive); setPricing(pr);
      if ((r.warnings || []).some((w) => w.level === "blocking")) toast.warning("Saved as a draft — see the provider limitation below");
      else toast.success("Event & attribution settings saved");
      loadMeas();
      onSaved && onSaved(r.growth_plan);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); } finally { setBusy(false); }
  };

  const advance = async (key, target) => {
    // 'Results Available' never fabricates results — it requires a validated provider study
    // or audience ID, collected through the operator Live Study Link panel.
    if (target === "Results Available") {
      setStudyModal({ key, study_id: "", audience_id: "", note: "" });
      return;
    }
    try {
      const r = await sr.advanceMeasurement(planId, { add_on: key, target_status: target });
      setMeas((m) => ({ ...m, ...r.measurement }));
      toast.success(`${key === "event_venue" ? "Event & Venue" : "Foot Traffic"} → ${target}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update status"); }
  };

  const submitStudyLink = async () => {
    const { key, study_id, audience_id, note } = studyModal;
    if (!String(study_id).trim() && !String(audience_id).trim()) {
      toast.error("Enter a validated provider study ID or audience ID.");
      return;
    }
    setLinking(true);
    try {
      const r = await sr.advanceMeasurement(planId, {
        add_on: key, target_status: "Results Available",
        study_id: String(study_id).trim() || null,
        audience_id: String(audience_id).trim() || null,
        note: String(note).trim() || null,
      });
      setMeas((m) => ({ ...m, ...r.measurement }));
      setStudyModal(null);
      toast.success("Results linked — status is now Results Available");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't link results"); } finally { setLinking(false); }
  };

  const toggleSample = async (key, clear) => {
    try {
      const r = await sr.loadSampleMeasurement(planId, { add_on: key, clear });
      setMeas((m) => ({ ...m, ...r.measurement }));
      toast.success(clear ? "Sample results cleared" : "Sample results loaded (demo)");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update sample data"); }
  };

  const Toggle = ({ on, setOn, icon, title, desc, pct }) => (
    <div className="sr-card" style={{ padding: 14, border: on ? "2px solid var(--blue)" : "1px solid var(--border)", background: on ? "var(--blue-light)" : "#fff" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
        <div style={{ display: "flex", gap: 10 }}>
          <div style={{ color: "var(--blue)" }}>{icon}</div>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}><strong style={{ fontSize: 14 }}>{title}</strong><span className="sr-badge sr-badge-gold" style={{ fontSize: 10 }}>+{pct}% of media</span></div>
            <p className="sr-muted" style={{ fontSize: 12, marginTop: 3, maxWidth: 440 }}>{desc}</p>
          </div>
        </div>
        <button type="button" role="switch" aria-checked={on} disabled={!editable}
          onClick={() => setOn(!on)} data-testid={title.includes("Event") ? "et-toggle-event" : "et-toggle-foottraffic"}
          style={{ width: 46, height: 26, borderRadius: 13, border: "none", cursor: editable ? "pointer" : "not-allowed", background: on ? "var(--blue)" : "#CBD5E1", position: "relative", flexShrink: 0 }}>
          <span style={{ position: "absolute", top: 3, left: on ? 23 : 3, width: 20, height: 20, borderRadius: "50%", background: "#fff", transition: "left .15s" }} />
        </button>
      </div>
    </div>
  );

  return (
    <div className="sr-card" data-testid="event-targeting" style={{ padding: 18 }}>
      <div className="sr-eyebrow" style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 4 }}><Ticket size={13} /> Event &amp; Venue Targeting · Foot Traffic Attribution</div>
      <p className="sr-muted" style={{ fontSize: 12, marginBottom: 14 }}>Premium add-ons. Toggling updates the quote only — it never charges or launches.</p>

      <div style={{ display: "grid", gap: 10 }}>
        <Toggle on={eventOn} setOn={setEventOn} icon={<MapPin size={20} />} title="Event &amp; Venue Targeting" pct={rates.event_venue_pct}
          desc="Reach eligible audiences associated with a venue during specific event dates and hours, including supported post-event follow-up." />
        <Toggle on={ftOn} setOn={setFtOn} icon={<Footprints size={20} />} title="Foot Traffic Attribution" pct={rates.foot_traffic_pct}
          desc="Measure eligible visits to a selected destination after campaign ad exposure. Works for any visits-driving campaign." />
      </div>

      {ftOn && (
        <div data-testid="et-ft-disclaimer" style={{ display: "flex", gap: 8, marginTop: 10, background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 8, padding: "8px 10px", fontSize: 12, color: "#1E40AF" }}>
          <Info size={14} style={{ flexShrink: 0, marginTop: 1 }} /> Foot traffic attribution typically reflects only about 40–60% of actual visits. Many people don't share location data in their privacy settings, so your real visits are likely higher — treat this as a directional lift measure, not a complete count.
        </div>
      )}

      {/* Provider capability honesty */}
      {caps && (
        <details data-testid="et-capabilities" style={{ marginTop: 12 }}>
          <summary style={{ cursor: "pointer", fontSize: 12, color: "var(--muted)" }}>Provider: {caps.provider} · {caps.connected ? "connected" : "not connected"} — what's supported</summary>
          <div style={{ display: "grid", gap: 6, marginTop: 8 }}>
            {caps.capabilities.map((c) => (
              <div key={c.key} style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12 }}>
                <SupportPill v={c.supported} />
                <div><strong>{c.label}.</strong> <span className="sr-muted">{c.note}</span></div>
              </div>
            ))}
          </div>
        </details>
      )}

      {eventOn && (
        <div data-testid="et-event-config" style={{ marginTop: 16, borderTop: "1px solid var(--border)", paddingTop: 14, display: "grid", gap: 14 }}>
          {!historicalSupported && (
            <div style={{ display: "flex", gap: 8, background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 8, padding: "8px 10px", fontSize: 12, color: "#92400E" }}>
              <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} /> Historical event-location audiences aren't available on the connected provider — starting today does not retroactively capture past devices or impressions. Use upcoming/ongoing dates, or an operator can attach a supported historical study.
            </div>
          )}

          {/* Campaign approach */}
          <div>
            <label className="sr-label">Campaign approach</label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {APPROACHES.map((a) => (
                <button key={a.key} type="button" onClick={() => setApproach(a.key)} data-testid={`et-approach-${a.key}`}
                  className={`sr-btn sr-btn-sm ${approach === a.key ? "sr-btn-primary" : "sr-btn-ghost"}`}>{a.label}</button>
              ))}
            </div>
          </div>

          {/* Audience source — explicit, never silently substituted */}
          <div>
            <label className="sr-label">Audience source</label>
            <div style={{ display: "grid", gap: 8 }}>
              {SOURCES.map((s) => (
                <button key={s.key} type="button" onClick={() => setSource(s.key)} data-testid={`et-source-${s.key}`}
                  style={{ textAlign: "left", border: source === s.key ? "2px solid var(--blue)" : "1px solid var(--border)", background: source === s.key ? "var(--blue-light)" : "#fff", borderRadius: 10, padding: 10, cursor: "pointer" }}>
                  <strong style={{ fontSize: 13 }}>{s.label}</strong>
                  <p className="sr-muted" style={{ fontSize: 12, marginTop: 2 }}>{s.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Venues */}
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <label className="sr-label" style={{ margin: 0 }}>Venues &amp; schedules</label>
              <button type="button" className="sr-btn sr-btn-ghost sr-btn-sm" onClick={addVenue} data-testid="et-add-venue"><Plus size={13} /> Add venue</button>
            </div>
            {venues.map((v, i) => (
              <div key={i} className="sr-card" data-testid={`et-venue-${i}`} style={{ padding: 12, marginTop: 8, background: "#FAFBFC" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <label className="sr-field-label">Event name<input className="sr-input" value={v.name} onChange={(e) => updVenue(i, "name", e.target.value)} data-testid={`et-venue-name-${i}`} /></label>
                  <label className="sr-field-label">Venue name &amp; address<input className="sr-input" value={v.address} onChange={(e) => updVenue(i, "address", e.target.value)} data-testid={`et-venue-address-${i}`} /></label>
                  <label className="sr-field-label">Latitude<input className="sr-input" type="number" value={v.lat} onChange={(e) => updVenue(i, "lat", e.target.value)} data-testid={`et-venue-lat-${i}`} /></label>
                  <label className="sr-field-label">Longitude<input className="sr-input" type="number" value={v.lng} onChange={(e) => updVenue(i, "lng", e.target.value)} data-testid={`et-venue-lng-${i}`} /></label>
                  <label className="sr-field-label">Radius (meters)<input className="sr-input" type="number" value={v.radius_m} onChange={(e) => updVenue(i, "radius_m", e.target.value)} data-testid={`et-venue-radius-${i}`} /></label>
                  <label className="sr-field-label">Venue time zone
                    <select className="sr-input" value={v.tz} onChange={(e) => updVenue(i, "tz", e.target.value)} data-testid={`et-venue-tz-${i}`}>{TZS.map((t) => <option key={t} value={t}>{t}</option>)}</select>
                  </label>
                  <label className="sr-field-label">Event start date<input className="sr-input" type="date" value={v.start_date} onChange={(e) => updVenue(i, "start_date", e.target.value)} data-testid={`et-venue-start-${i}`} /></label>
                  <label className="sr-field-label">Event end date<input className="sr-input" type="date" value={v.end_date} onChange={(e) => updVenue(i, "end_date", e.target.value)} data-testid={`et-venue-end-${i}`} /></label>
                  <label className="sr-field-label">Daily start time<input className="sr-input" type="time" value={v.daily_start} onChange={(e) => updVenue(i, "daily_start", e.target.value)} data-testid={`et-venue-dstart-${i}`} /></label>
                  <label className="sr-field-label">Daily end time<input className="sr-input" type="time" value={v.daily_end} onChange={(e) => updVenue(i, "daily_end", e.target.value)} data-testid={`et-venue-dend-${i}`} /></label>
                </div>
                {v.lat && v.lng && (
                  <div style={{ marginTop: 8 }}>
                    <iframe title={`map-${i}`} data-testid={`et-venue-map-${i}`} width="100%" height="150" style={{ border: "1px solid var(--border)", borderRadius: 8 }}
                      src={`https://www.openstreetmap.org/export/embed.html?bbox=${Number(v.lng) - 0.01},${Number(v.lat) - 0.01},${Number(v.lng) + 0.01},${Number(v.lat) + 0.01}&layer=mapnik&marker=${v.lat},${v.lng}`} />
                    <div className="sr-muted" style={{ fontSize: 11, marginTop: 4 }}>Pin: {Number(v.lat).toFixed(4)}, {Number(v.lng).toFixed(4)} · {v.start_date || "?"}→{v.end_date || "?"} · {v.daily_start}–{v.daily_end} {v.tz}</div>
                  </div>
                )}
                {venues.length > 1 && <button type="button" className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => removeVenue(i)} style={{ marginTop: 6, color: "#B42318" }} data-testid={`et-remove-venue-${i}`}><Trash2 size={13} /> Remove</button>}
              </div>
            ))}
          </div>

          {/* Three independent date settings */}
          {(approach === "after" || approach === "both") && (
            <div data-testid="et-dates" style={{ display: "grid", gap: 10, background: "#F8FAFC", border: "1px solid var(--border)", borderRadius: 10, padding: 12 }}>
              <div className="sr-eyebrow"><CalendarClock size={12} /> Qualification · Retention · Advertising (kept separate)</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <label className="sr-field-label">Qualification window start<input className="sr-input" type="datetime-local" value={qual.start} onChange={(e) => setQual((s) => ({ ...s, start: e.target.value }))} data-testid="et-qual-start" /></label>
                <label className="sr-field-label">Qualification window end<input className="sr-input" type="datetime-local" value={qual.end} onChange={(e) => setQual((s) => ({ ...s, end: e.target.value }))} data-testid="et-qual-end" /></label>
                <label className="sr-field-label">Audience retention (days)<input className="sr-input" type="number" value={retentionDays} onChange={(e) => setRetentionDays(e.target.value)} data-testid="et-retention" /></label>
                <div />
                <label className="sr-field-label">Advertising flight start<input className="sr-input" type="datetime-local" value={flight.start} onChange={(e) => setFlight((s) => ({ ...s, start: e.target.value }))} data-testid="et-flight-start" /></label>
                <label className="sr-field-label">Advertising flight end<input className="sr-input" type="datetime-local" value={flight.end} onChange={(e) => setFlight((s) => ({ ...s, end: e.target.value }))} data-testid="et-flight-end" /></label>
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {[7, 14, 30].map((d) => <button key={d} type="button" className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setRetentionDays(d)} data-testid={`et-retention-${d}`}>{d}-day retention</button>)}
              </div>
            </div>
          )}

          {/* T3 — Linked delivery components (share one budget) */}
          <div data-testid="et-delivery" style={{ border: "1px solid var(--border)", borderRadius: 10, padding: 12 }}>
            <div className="sr-eyebrow" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Layers size={12} /> Linked delivery components</div>
            <p className="sr-muted" style={{ fontSize: 12, marginTop: 4 }}>
              {approach === "both"
                ? "Your event media splits across two linked components that share one budget — they always total 100%, so spend is never double-counted."
                : approach === "during"
                  ? "A single event-day component runs during the event window."
                  : "A single follow-up component retargets the qualified audience after the event."}
            </p>
            {approach === "both" && (
              <div style={{ marginTop: 10 }} data-testid="et-delivery-split">
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, fontWeight: 600, color: "var(--navy)" }}>
                  <span>Event-day {Math.round(eventShare)}%</span><span>Follow-up {Math.round(100 - eventShare)}%</span>
                </div>
                <input type="range" min="0" max="100" step="5" value={eventShare} disabled={!editable} onChange={(e) => setEventShare(Number(e.target.value))} data-testid="et-delivery-slider" style={{ width: "100%" }} />
              </div>
            )}
            <div style={{ display: "grid", gap: 8, marginTop: 10 }}>
              {delivery.components.map((c) => (
                <div key={c.key} data-testid={`et-component-${c.key}`} style={{ border: "1px solid var(--border)", borderRadius: 10, padding: 10, display: "flex", justifyContent: "space-between", gap: 10 }}>
                  <div>
                    <strong style={{ fontSize: 13 }}>{c.name}</strong>
                    <span className="sr-badge sr-badge-blue" style={{ marginLeft: 6, fontSize: 10 }}>{c.share_pct}%</span>
                    <p className="sr-muted" style={{ fontSize: 12, marginTop: 3 }}>{c.description}</p>
                    <p className="sr-muted" style={{ fontSize: 11, marginTop: 2 }}>Window: {String(c.window?.start || "").slice(0, 10) || "—"} → {String(c.window?.end || "").slice(0, 10) || "—"}</p>
                  </div>
                  <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    <div style={{ fontWeight: 800, color: "var(--navy)" }} data-testid={`et-component-budget-${c.key}`}>{usd(c.budget)}</div>
                    <div className="sr-muted" style={{ fontSize: 11 }}>of {usd(eventMedia)}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="sr-muted" style={{ fontSize: 11, marginTop: 8 }} data-testid="et-delivery-total">Total allocated: {usd(delivery.allocated)} · one shared budget of {usd(eventMedia)}.</div>
          </div>
        </div>
      )}

      {/* Warnings */}
      {warnings.length > 0 && (
        <div data-testid="et-warnings" style={{ marginTop: 12, display: "grid", gap: 6 }}>
          {warnings.map((w, i) => (
            <div key={i} style={{ display: "flex", gap: 8, fontSize: 12, padding: "8px 10px", borderRadius: 8, background: w.level === "blocking" ? "#FEF2F2" : "#FFFBEB", border: `1px solid ${w.level === "blocking" ? "#FECACA" : "#FDE68A"}`, color: w.level === "blocking" ? "#B42318" : "#92400E" }}>
              <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} /> {w.msg}
            </div>
          ))}
        </div>
      )}

      {/* Live price summary */}
      {pricing && (eventOn || ftOn) && (
        <div data-testid="et-price-summary" style={{ marginTop: 14, borderTop: "1px solid var(--border)", paddingTop: 12 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, marginBottom: 8 }}>
            <input type="checkbox" checked={inclusive} onChange={(e) => setInclusive(e.target.checked)} data-testid="et-inclusive" /> My budget already includes these premiums
          </label>
          <div style={{ display: "grid", gap: 4, fontSize: 13 }}>
            <Line label="Media spend" value={usd(pricing.media)} testid="et-media" />
            {pricing.premiums.event_venue.enabled && <Line label={`Event & Venue Targeting — ${pricing.premiums.event_venue.pct}%`} value={usd(pricing.premiums.event_venue.amount)} testid="et-event-premium" />}
            {pricing.premiums.foot_traffic.enabled && <Line label={`Foot Traffic Attribution — ${pricing.premiums.foot_traffic.pct}%`} value={usd(pricing.premiums.foot_traffic.amount)} testid="et-ft-premium" />}
            <div style={{ borderTop: "1px solid var(--border)", marginTop: 4, paddingTop: 4 }}>
              <Line label={inclusive ? "Total (inclusive of premiums)" : "Subtotal before other fees/taxes"} value={usd(pricing.total_customer_cost)} strong testid="et-total" />
            </div>
          </div>
          <p className="sr-muted" style={{ fontSize: 11, marginTop: 6 }}>Projected premiums. For spend-based billing we reconcile to actual eligible media and show the adjustment. Features aren't charged until active.</p>
        </div>
      )}

      {/* Honest measurement status machine (per add-on) */}
      {meas && (eventOn || ftOn) && (
        <div data-testid="et-measurement" style={{ marginTop: 14, borderTop: "1px solid var(--border)", paddingTop: 12 }}>
          <div className="sr-eyebrow" style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 8 }}><Clock size={12} /> Measurement status</div>
          <div style={{ display: "grid", gap: 10 }}>
            {[["event_venue", "Event & Venue", eventOn], ["foot_traffic", "Foot Traffic", ftOn]].filter(([, , on]) => on).map(([key, label]) => {
              const m = meas[key]; if (!m) return null;
              return (
                <div key={key} data-testid={`et-status-${key}`} style={{ border: "1px solid var(--border)", borderRadius: 10, padding: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <strong style={{ fontSize: 13 }}>{label}</strong>
                    <span className={`sr-badge ${TONE[m.tone] || "sr-badge-gray"}`} data-testid={`et-status-badge-${key}`}>{m.label}</span>
                    {m.is_sample && <span className="sr-badge sr-badge-amber" style={{ fontSize: 10 }} data-testid={`et-status-sample-${key}`}><Info size={10} /> SAMPLE</span>}
                  </div>
                  <p className="sr-muted" style={{ fontSize: 12, marginTop: 5 }}>{m.description} <strong style={{ color: "var(--navy)" }}>Next:</strong> {m.what_next}</p>
                  {(m.study_id || m.audience_id) && (
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }} data-testid={`et-study-link-${key}`}>
                      {m.study_id && <span className="sr-badge sr-badge-blue" style={{ fontSize: 10 }}><Link2 size={10} /> Study&nbsp;{m.study_id}</span>}
                      {m.audience_id && <span className="sr-badge sr-badge-blue" style={{ fontSize: 10 }}><Link2 size={10} /> Audience&nbsp;{m.audience_id}</span>}
                    </div>
                  )}
                  {m.operator_note && <p className="sr-muted" style={{ fontSize: 11, marginTop: 4, fontStyle: "italic" }}>Operator note: {m.operator_note}</p>}
                  {(meas.can_advance || (key === "foot_traffic" && meas.can_load_sample)) && (
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                      {meas.can_advance && (m.allowed_next || []).map((ns) => (
                        <button key={ns} type="button" className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => advance(key, ns)} data-testid={`et-advance-${key}-${ns.toLowerCase().replace(/[^a-z]+/g, "-")}`}>→ {ns}</button>
                      ))}
                      {key === "foot_traffic" && meas.can_load_sample && (
                        m.is_sample
                          ? <button type="button" className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => toggleSample(key, true)} data-testid="et-sample-clear" style={{ color: "#B42318" }}>Clear sample</button>
                          : <button type="button" className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => toggleSample(key, false)} data-testid="et-sample-load"><Info size={12} /> Load sample results (demo)</button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <p className="sr-muted" style={{ fontSize: 11, marginTop: 8 }}>Statuses reflect real delivery. We never invent audiences or attribution — results appear from a live, operator-validated study, or as clearly-labeled sample data.</p>
        </div>
      )}

      {editable && (
        <button type="button" className="sr-btn sr-btn-primary" onClick={save} disabled={busy} data-testid="et-save" style={{ marginTop: 14 }}>
          {busy ? <Loader2 size={15} className="sr-spin" /> : <><Save size={15} /> Save targeting &amp; premiums</>}
        </button>
      )}

      {studyModal && (
        <div data-testid="study-link-modal" onClick={() => !linking && setStudyModal(null)}
          style={{ position: "fixed", inset: 0, zIndex: 90, background: "rgba(10,18,28,.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div className="sr-card" onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 460, padding: 22 }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
              <div>
                <div className="sr-eyebrow" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><FlaskConical size={13} /> Link validated results</div>
                <h3 style={{ fontSize: 18, marginTop: 6 }}>{studyModal.key === "event_venue" ? "Event & Venue Targeting" : "Foot Traffic Attribution"}</h3>
              </div>
              <button type="button" className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => !linking && setStudyModal(null)} data-testid="study-link-close"><X size={15} /></button>
            </div>
            <p className="sr-muted" style={{ fontSize: 12, marginTop: 6 }}>
              Paste the real provider study or audience ID to advance this add-on to <strong style={{ color: "var(--navy)" }}>Results Available</strong>. We never fabricate attribution — enter at least one ID. For a sandbox walkthrough, use <em>Load sample results</em> instead.
            </p>
            <div style={{ display: "grid", gap: 12, marginTop: 14 }}>
              <label className="sr-field-label" style={{ display: "block" }}>
                <span className="sr-label">Provider study ID</span>
                <input className="sr-input" value={studyModal.study_id} autoFocus
                  onChange={(e) => setStudyModal((s) => ({ ...s, study_id: e.target.value }))}
                  placeholder="e.g. FT-STUDY-48213" data-testid="study-link-study-id" />
              </label>
              <label className="sr-field-label" style={{ display: "block" }}>
                <span className="sr-label">Audience ID <span className="sr-muted" style={{ fontWeight: 400 }}>(optional)</span></span>
                <input className="sr-input" value={studyModal.audience_id}
                  onChange={(e) => setStudyModal((s) => ({ ...s, audience_id: e.target.value }))}
                  placeholder="e.g. AUD-99021" data-testid="study-link-audience-id" />
              </label>
              <label className="sr-field-label" style={{ display: "block" }}>
                <span className="sr-label">Operator note <span className="sr-muted" style={{ fontWeight: 400 }}>(optional)</span></span>
                <textarea className="sr-textarea" rows={2} value={studyModal.note}
                  onChange={(e) => setStudyModal((s) => ({ ...s, note: e.target.value }))}
                  placeholder="Context for this measurement window" data-testid="study-link-note" />
              </label>
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
              <button type="button" className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setStudyModal(null)} disabled={linking} data-testid="study-link-cancel">Cancel</button>
              <button type="button" className="sr-btn sr-btn-primary sr-btn-sm" onClick={submitStudyLink} disabled={linking} data-testid="study-link-submit">
                {linking ? <Loader2 size={14} className="sr-spin" /> : <><Link2 size={14} /> Link results</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Line({ label, value, strong, testid }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", fontWeight: strong ? 700 : 400 }} data-testid={testid}>
      <span className={strong ? "" : "sr-muted"}>{label}</span><span>{value}</span>
    </div>
  );
}
